import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";
import { apiErr } from "../lib/api";
import { firstAllowedRoute } from "../lib/adminSections";
import Logo from "../components/Logo";
import LanguageSwitcher from "../components/LanguageSwitcher";
import Seo from "../components/Seo";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const { t } = useLang();
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success(t("lg_welcome"));
      // Un sous-administrateur arrive directement sur sa première section autorisée.
      nav(u.role === "admin" ? firstAllowedRoute(u) : u.role === "ambassador" ? "/ambassadeur/espace" : u.role === "partner" ? "/partenaire/espace" : "/app");
    } catch (err) {
      toast.error(apiErr(err));
    } finally { setLoading(false); }
  };

  return (
    <div className="grain grid min-h-screen bg-void lg:grid-cols-2">
      <Seo title="Connexion" description="Connectez-vous à votre espace APP CHALLENGE ACADEMY pour suivre vos idées et vos projets." path="/connexion" />
      <div className="absolute right-4 top-4 z-20"><LanguageSwitcher /></div>
      <div className="relative hidden overflow-hidden border-r border-white/10 bg-surface lg:block">
        <div className="pointer-events-none absolute -top-20 -left-20 h-96 w-96 rounded-full bg-electric/20 blur-[120px]" />
        <div className="relative flex h-full flex-col justify-between p-12">
          <Link to="/"><Logo /></Link>
          <div>
            <div className="text-[11px] uppercase tracking-[0.25em] text-cyan">{t("lg_title")}</div>
            <h2 className="mt-4 font-display text-4xl font-900 leading-tight text-white">
              {t("lg_side_t")}
            </h2>
            <p className="mt-6 max-w-sm text-sm text-ink-secondary">
              {t("lg_side_d")}
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center px-5 py-16 sm:px-12">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
          <Link to="/" className="mb-8 inline-flex items-center gap-1.5 text-sm text-ink-secondary hover:text-white lg:hidden">
            <ArrowLeft size={15} /> {t("back")}
          </Link>
          <h1 className="font-display text-3xl font-700 text-white">{t("lg_title")}</h1>
          <p className="mt-2 text-sm text-ink-secondary">{t("lg_sub")}</p>
          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="login-form">
            <Field label={t("f_email")} type="email" value={email} onChange={setEmail} testid="login-email" autoFocus />
            <Field label={t("f_pwd")} type="password" value={password} onChange={setPassword} testid="login-password" />
            <button
              type="submit" disabled={loading} data-testid="login-submit"
              className="group mt-2 inline-flex w-full items-center justify-center gap-2 bg-cyan px-6 py-4 text-sm font-600 uppercase tracking-wide text-void transition-all hover:glow-cyan disabled:opacity-60"
            >
              {loading ? t("lg_submitting") : t("lg_submit")}
              <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
            </button>
          </form>
          <p className="mt-6 text-sm text-ink-secondary">
            {t("lg_no")} <Link to="/inscription" data-testid="to-register" className="text-cyan hover:underline">{t("lg_create")}</Link>
          </p>
        </motion.div>
      </div>
    </div>
  );
}

function Field({ label, type = "text", value, onChange, testid, autoFocus }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-[11px] uppercase tracking-[0.15em] text-white">{label}</span>
      <input
        required type={type} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} autoFocus={autoFocus}
        className="w-full rounded-xl border border-white/15 bg-white/[0.06] px-4 py-3.5 text-sm text-white outline-none transition-colors focus:border-cyan focus:ring-1 focus:ring-cyan"
      />
    </label>
  );
}
